import { useMemo, useState } from 'react';

import { isEmpty } from 'lodash';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Input, Slider, Tooltip } from 'antd';
import { SliderMarks } from 'antd/lib/slider';
import { getWalletSliderMaxValuePosition, RootState } from '@share/utils';
import { ILoginState } from '@share/store/slices';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faInfoCircle, faWallet } from '@fortawesome/free-solid-svg-icons';

import './style.scss';
import { GetCapitalWalletLog } from '@utils';

interface IMapStateToProps {
  loginStore: ILoginState;
}

interface IProps extends IMapStateToProps, WrappedComponentProps {
  display: boolean;

  selectedClientCash: number | null | undefined;

  titleLabel?: string;

  onSelectClientCash: (selectedClientCash: number | null | undefined) => void;
}

function WalletSearchSliderComponent(props: IProps) {
  const [collapse, setCollapse] = useState(false);
  // Holds the value while the user is actively dragging/clicking the slider. We update this
  // locally on every `onChange` but only push to Redux once, on release (`onAfterChange`),
  // so a drag doesn't fire a Redux dispatch per pixel.
  const [draggingValue, setDraggingValue] = useState<number | null>(null);
  // Holds the raw text while the user is typing in the paired input. Without this, a controlled
  // numeric value re-renders `"749."` back to `"749"` mid-type, so the decimal point can never
  // be entered. `null` means "not editing" and the input derives its text from the slider value.
  const [inputText, setInputText] = useState<string | null>(null);

  const { display, selectedClientCash, titleLabel, loginStore, onSelectClientCash, intl } = props;
  const { account, userWalletData, lifeStyle } = loginStore;

  const walletNoDecimals = account?.walletNoDecimals;
  const background: any = account?.buttonColor ? account?.buttonColor : '#0081FE';

  const maxWalletAmount = userWalletData?.balance;
  const maxSlider = maxWalletAmount ?
                      walletNoDecimals ?
                        Math.floor(maxWalletAmount) :
                        maxWalletAmount ? Math.floor(maxWalletAmount * 100) / 100 : 0 :
                      0;

  // Every balance/points amount rendered in this component is formatted through this helper so it
  // honours the account's "No decimals allowed" preference: integers when it's on, otherwise a
  // fixed two decimals. Regular wallet only.
  const formatWalletAmount = (amount?: number | null): string => {
    const value = amount ?? 0;
    return walletNoDecimals
      ? Math.floor(value).toLocaleString('en-US')
      : (Math.floor(value * 100) / 100).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  };

  // Memoize marks so its identity is stable across renders. rc-slider derives its
  // internal `rawValues` from the `marks` object identity; a brand-new object on every
  // render makes its cache-sync effect re-fire each render, which can spiral into React
  // error #185 ("Maximum update depth exceeded") while dragging the slider.
  const marks: SliderMarks = useMemo(() => {
    const result: SliderMarks = {
      0: {
        style: {
          transform: 'translate(-4px, -40px)'
        },
        label: '0',
      }
    };

    if (maxSlider) {
      result[maxSlider] = {
        style: {
          transform: `translate(${walletNoDecimals ? getWalletSliderMaxValuePosition(maxSlider) :'-27'}px, -40px)`,
          left: 'calc(100% - 5px)',
        },
        label: walletNoDecimals
          ? maxSlider.toLocaleString('en-US')
          : maxSlider.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      };
    }

    return result;
  }, [maxSlider, walletNoDecimals]);

  if (!display) {
    return null;
  }

  // Value handed to the controlled Slider, clamped to [0, maxSlider] and aligned to the step.
  const committedValue = !selectedClientCash || selectedClientCash < 0
    ? 0
    : walletNoDecimals
      ? Math.round(Math.min(selectedClientCash, maxSlider))
      : Math.round(Math.min(selectedClientCash, maxSlider) * 100) / 100;

  // While dragging we drive the slider from local state and do NOT dispatch on every tick.
  // Dispatching each intermediate value re-renders the whole results list per pixel which,
  // with rc-slider 10.0.1, spirals into "Maximum update depth exceeded". The value is
  // committed once, on release, via onAfterChange below.
  const sliderValue = draggingValue != null ? draggingValue : committedValue;

  const handleCollapse = () => {
    setCollapse(!collapse);
  }

  const logo = GetCapitalWalletLog(lifeStyle);
  const isCapital = account?.isCapitalVacations;

  // The wallet service can be slow; `userWalletData` stays null until it resolves. While it's
  // still loading we keep only the title + logo and show a friendly loading message instead of
  // rendering the (otherwise disabled) collapse, slider and controls.
  const isLoading = !userWalletData;

  const explanationTooltipText = maxWalletAmount
    ? intl.formatMessage(
        { id: 'wallet.client.cash.search_slider.message' },
        {
          amount: (userWalletData?.maxClientCashPercentageAllow || 0) * 100,
          amountActive: formatWalletAmount(userWalletData?.balance),
          clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName,
          strong: (message: string) => (<strong>{message}</strong>)
}
      )
    : intl.formatMessage(
        { id: 'wallet.client.cash.no_client_cash.message' },
        { amountActive: formatWalletAmount(userWalletData?.balance), clientCash: account?.walletClientCashName }
      );

  return (
    <div className="wallet-search-slider container-fluid" style={{ position: 'relative' }}>
      {!isLoading && (
        <div className="wallet-search-slider__collapse" onClick={handleCollapse}>
          <FontAwesomeIcon icon={collapse ? faAngleDown : faAngleUp} />
        </div>
      )}

      <div className="row">
        <div className={`wallet-search-slider__header col-md-12 ${isCapital ? 'capital-header-slider' : ''}`}>
          <p className={`wallet-search-slider__title ${collapse ? 'collapsed' : ''} ${isCapital ? 'capital' : ''}`} style={{ color: background }}>
            <FormattedMessage
              id={titleLabel ? titleLabel : 'wallet.client.cash.want_use'}
              values={{ clientCashName: account?.walletClientCashName }}
            />
            {!isLoading && (
              <Tooltip title={explanationTooltipText} placement="top">
                <span className="wallet-search-slider__info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
              </Tooltip>
            )}
          </p>
          {isCapital ? <img className="wallet-search-slider__logo" src={logo} alt="LIFESTYLES" /> : null}
        </div>

        {isLoading ? (
          <div className="wallet-search-slider__loading col-md-12">
            <span className="wallet-search-slider__loading-text">
              <FormattedMessage id="wallet.client.cash.loading" />
            </span>
            <span className="wallet-search-slider__loading-dots" aria-hidden="true">
              <span className="wallet-search-slider__loading-dot">.</span>
              <span className="wallet-search-slider__loading-dot">.</span>
              <span className="wallet-search-slider__loading-dot">.</span>
            </span>
          </div>
        ) : !collapse ? (
          <>
            <div className="wallet-search-slider__balance-badge">
              <span>
                <FontAwesomeIcon icon={faWallet} className="wallet-search-slider__balance-icon" style={{ color: background }} />
                <FormattedMessage
                  id="wallet.client.cash.search_slider.message2"
                  values={{
                    amountActive: formatWalletAmount(maxSlider),
                    clientCash: account?.walletClientCashName || 'Points'
                  }}
                  />
              </span>
            </div>

            <div className={`wallet-search-slider__controls col-md-12 ${isCapital ? 'capital' : ''}`}>
              <div className="wallet-search-slider__slider-wrap">
                <Slider
                  min={0}
                  max={maxSlider}
                  marks={marks}
                  defaultValue={0}
                  step={walletNoDecimals ? 1 : 0.01}
                  tooltip={{ open: false }}
                  value={sliderValue}
                  onChange={(value: any) => setDraggingValue(value)}
                  onAfterChange={(value: any) => {
                    setDraggingValue(null);
                    onSelectClientCash(value);
                  }}
                  trackStyle={{ backgroundColor: background }}
                  handleStyle={{ borderColor: background, backgroundColor: background }}
                  disabled={!!maxWalletAmount && maxWalletAmount <= 0}
                  style={{ marginTop: '30px' }}
                  className="wallet-search-slider__slider"
                />
              </div>
              <Input
                value={inputText != null ? inputText : (sliderValue && sliderValue >= 0 ? formatWalletAmount(sliderValue) : '')}
                className={`room-info__client-cash-input ${(selectedClientCash || 0) > (maxWalletAmount || 0) ? 'invalid' : ''}`}
                onChange={e => {
                  // Strip separators; keep the decimal point only when decimals are allowed, and
                  // cap it to a single dot with at most two fraction digits.
                  let raw = e.target.value.replace(/,/g, '');
                  raw = walletNoDecimals ? raw.replace(/[^\d]/g, '') : raw.replace(/[^\d.]/g, '');
                  if (!walletNoDecimals) {
                    const dot = raw.indexOf('.');
                    if (dot !== -1) {
                      raw = raw.slice(0, dot + 1) + raw.slice(dot + 1).replace(/\./g, '').slice(0, 2);
                    }
                  }
                  setInputText(raw);
                  const parsed = isEmpty(raw) || raw === '.' ? null : Number(raw);
                  onSelectClientCash(parsed != null && Number.isNaN(parsed) ? null : parsed);
                }}
                onBlur={() => setInputText(null)}
                onKeyPress={event => {
                  const allowed = walletNoDecimals ? /[0-9]/ : /[0-9.]/;
                  if (!allowed.test(event.key)) event.preventDefault();
                }}
                disabled={!!maxWalletAmount && maxWalletAmount <= 0}
              />
            </div>

            {(selectedClientCash || 0) > (maxWalletAmount || 0) && (
              <div className="wallet-search-slider__note invalid col-md-12" style={{ marginBottom: '10px' }}>
                <FormattedMessage id="wallet.client.cash.invalid" values={{ amount: formatWalletAmount(maxSlider), clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName }} />
              </div>)}

            {!!selectedClientCash && walletNoDecimals && selectedClientCash <= (maxWalletAmount || 0) && Math.floor(selectedClientCash) !== selectedClientCash && (
              <div className="wallet-input__note invalid col-md-12" style={{ marginBottom: '10px' }}>
                <FormattedMessage id="wallet.client.cash.only_decimals" values={{ clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName }} />
              </div>)}

            <div className={`wallet-search-slider__explanation col-md-12 ${isCapital ? 'capital' : ''}`}>
              {maxWalletAmount ? (
                <FormattedMessage
                  id="wallet.client.cash.search_slider.message"
                  values={{
                    amount: (userWalletData?.maxClientCashPercentageAllow || 0) * 100,
                    amountActive: formatWalletAmount(userWalletData?.balance),
                    clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName,
                    strong: (message: string) => (<strong>{message}</strong>)
                  }}
                />
              ) : (
                <FormattedMessage
                  id="wallet.client.cash.no_client_cash.message"
                  values={{ amountActive: formatWalletAmount(userWalletData?.balance), clientCash: account?.walletClientCashName }}
                />
              )}
            </div>
          </>) : null}
      </div>
    </div>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore
  };
};

export const WalletSearchSlider = connect(mapStateToProps)(injectIntl(WalletSearchSliderComponent));

